import {ref, computed, inject} from "vue";
import {defineStore, storeToRefs} from "pinia";
import {useStorage} from "@vueuse/core";
import {oplitClient} from "@/api";
import {useSchedulingStore} from "@/stores/schedulingStore";
import {useNomenclatures} from "@/domains/parameters/composables/useNomenclatures";
import {useLocalStorage} from "@/composables/useLocalStorage";
import {
  type SchedulingComponentSidebarDetailsItem,
  type SchedulingComponentDetailsItem,
  type SchedulingComponentAnalytics,
  type SchedulingComponentsCurrentStocks,
} from "@oplit/shared-module";
import {OpenSnackbarFunction} from "@/interfaces";

export const useSchedulingComponentsStore = defineStore(
  "schedulingComponents",
  () => {
    const openSnackbar = inject<OpenSnackbarFunction>("openSnackbar");

    const {selectedSimulation, pgOpsModifications} = storeToRefs(
      useSchedulingStore(),
    );

    const {nomenclatureList} = useNomenclatures();
    const {buildStorageKey} = useLocalStorage(`schedulingComponentsStore`);

    const schedulingComponentsBreadcrumbs = useStorage<{ref_article: string}[]>(
      buildStorageKey("schedulingComponentsBreadcrumbs"),
      [],
    );
    const selectedSchedulingComponent = ref<{ref_article: string}>(
      schedulingComponentsBreadcrumbs.value.at(-1) ?? null,
    );
    const isLoadingSidebarComponentsDetails = ref<boolean>(false);
    const isLoadingComponentDetails = ref<boolean>(false);
    const isLoadingComponentAnalytics = ref<boolean>(false);
    const isLoadingComponentsCurrentStocks = ref<boolean>(false);

    /**
     * this is only used to display the "components" tab conditionally in OperationsRightBar
     */
    const articlesWithChildren = computed<Set<string>>(
      () =>
        new Set([
          ...nomenclatureList.value.map(({product_name}) => product_name),
        ]),
    );
    /**
     * array of dependencies for the selected scheduling component context
     * watched in components fetching data
     */
    const componentDetailsWatcher = computed(
      () =>
        [
          selectedSchedulingComponent.value,
          pgOpsModifications.value,
          selectedSimulation.value,
        ] as const,
    );
    /**
     * array of dependencies for the components stocks context
     * watched in components fetching data
     */
    const componentsCurrentStocksWatcher = computed(
      () => [selectedSimulation.value] as const,
    );

    function setSelectedSchedulingComponentAndUpdateBreadcrumbs(
      component: {
        ref_article: string;
      } | null,
    ): void {
      if (!component) {
        selectedSchedulingComponent.value = null;
        schedulingComponentsBreadcrumbs.value = [];
      } else {
        const {ref_article} = component;
        if (ref_article === selectedSchedulingComponent.value?.ref_article)
          return;

        selectedSchedulingComponent.value = {ref_article};
        schedulingComponentsBreadcrumbs.value.push({ref_article});
      }
    }
    async function fetchSidebarComponentsDetails(operation: {
      ref_article: string;
      day_date: string;
      of_id: string;
    }): Promise<SchedulingComponentSidebarDetailsItem[] | undefined> {
      try {
        isLoadingSidebarComponentsDetails.value = true;

        const [sidebarComponentsDetails, sidebarComponentsDetailsError] =
          await oplitClient.getSchedulingComponentsSidebarComponentsDetails({
            simulation_id: selectedSimulation.value.id,
            ref_article: operation.ref_article,
            day_date: operation.day_date,
            of_id: operation.of_id,
          });

        if (sidebarComponentsDetailsError) throw sidebarComponentsDetailsError;

        return sidebarComponentsDetails;
      } catch (error) {
        openSnackbar(null, null, error);
      } finally {
        isLoadingSidebarComponentsDetails.value = false;
      }
    }
    async function fetchComponentDetails(params: {
      end_date: string | null;
    }): Promise<SchedulingComponentDetailsItem[] | undefined> {
      try {
        isLoadingComponentDetails.value = true;

        const [componentDetails, componentDetailsEror] =
          await oplitClient.getSchedulingComponentsComponentDetails({
            ref_article: selectedSchedulingComponent.value.ref_article,
            simulation_id: selectedSimulation.value.id,
            end_date: params.end_date,
          });

        if (componentDetailsEror) throw componentDetailsEror;

        return componentDetails as SchedulingComponentDetailsItem[];
      } catch (error) {
        openSnackbar(null, null, error);
      } finally {
        isLoadingComponentDetails.value = false;
      }
    }
    async function fetchComponentAnalytics(): Promise<
      SchedulingComponentAnalytics | undefined
    > {
      try {
        isLoadingComponentAnalytics.value = true;

        const [componentAnalytics, componentAnalyticsError] =
          await oplitClient.getSchedulingComponentsComponentAnalytics({
            ref_article: selectedSchedulingComponent.value.ref_article,
            simulation_id: selectedSimulation.value.id,
          });

        if (componentAnalyticsError) throw componentAnalyticsError;

        return componentAnalytics;
      } catch (error) {
        openSnackbar(null, null, error);
      } finally {
        isLoadingComponentAnalytics.value = false;
      }
    }
    async function fetchComponentsCurrentStocks(params: {
      end_date: string | null;
      ref_articles?: string[];
    }): Promise<SchedulingComponentsCurrentStocks[] | undefined> {
      try {
        isLoadingComponentsCurrentStocks.value = true;

        const [componentsCurrentStocks, componentsCurrentStockError] =
          await oplitClient.getSchedulingComponentsComponentsCurrentStocks({
            simulation_id: selectedSimulation.value.id,
            end_date: params.end_date,
            ref_articles: params.ref_articles,
          });

        if (componentsCurrentStockError) throw componentsCurrentStockError;

        return componentsCurrentStocks;
      } catch (error) {
        openSnackbar(null, null, error);
      } finally {
        isLoadingComponentsCurrentStocks.value = false;
      }
    }
    async function fetchComponentInitialStock(): Promise<number> {
      try {
        const [componentInitialStock, componentInitialStockError] =
          await oplitClient.getSchedulingComponentsComponentInitialStock({
            simulation_id: selectedSimulation.value.id,
            ref_article: selectedSchedulingComponent.value.ref_article,
          });

        if (componentInitialStockError) throw componentInitialStockError;

        return componentInitialStock;
      } catch (error) {
        openSnackbar(null, null, error);
      }
    }

    return {
      schedulingComponentsBreadcrumbs,
      selectedSchedulingComponent,
      isLoadingSidebarComponentsDetails,
      isLoadingComponentDetails,
      isLoadingComponentAnalytics,
      isLoadingComponentsCurrentStocks,
      componentDetailsWatcher,
      componentsCurrentStocksWatcher,
      articlesWithChildren,
      setSelectedSchedulingComponentAndUpdateBreadcrumbs,
      fetchSidebarComponentsDetails,
      fetchComponentDetails,
      fetchComponentAnalytics,
      fetchComponentsCurrentStocks,
      fetchComponentInitialStock,
    };
  },
);
